import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Bot, User, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

export function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: '¡Hola! 👋 Soy el asistente de MKTN GUARDIANS. ¿En qué puedo ayudarte con tu marketing digital?',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Auto-scroll al final cuando hay nuevos mensajes
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const sendMessage = async (messageText?: string) => {
    const messageToSend = messageText || input;
    if (!messageToSend.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Preparar conversación para la IA (solo contenido, sin metadatos)
      const conversation = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          message: messageToSend,
          conversation: conversation
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error('Error al comunicarse con el servicio de IA');
      }

      // Verificar si la respuesta contiene un error interno
      if (data?.error) {
        console.error('AI service error:', data.error);
        throw new Error(data.response || 'Error interno del servicio de IA');
      }

      if (!data?.response) {
        throw new Error('Respuesta vacía del servicio de IA');
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.response,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (error: any) {
      console.error('Error sending message:', error);
      
      let errorDescription = "No se pudo enviar el mensaje. Inténtalo de nuevo.";
      let aiResponseText = 'Lo siento, hay un problema técnico. ¿Podrías intentar de nuevo? 🔧';
      
      if (error.message.includes('saturado') || error.message.includes('429')) {
        errorDescription = "El servicio está temporalmente saturado. Intenta en unos minutos.";
        aiResponseText = 'El servicio está temporalmente saturado. Por favor, intenta de nuevo en unos minutos. 🕐';
      } else if (error.message.includes('configuración') || error.message.includes('401')) {
        errorDescription = "Hay un problema con el servicio. Contacta al administrador.";
        aiResponseText = 'Hay un problema técnico con el servicio. Por favor, contacta directamente con nosotros. 📞';
      }
      
      toast({
        title: "Error en el chat",
        description: errorDescription,
        variant: "destructive"
      });

      // Mensaje de error de la IA
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponseText,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = [
    "¿Qué servicios ofrecen?",
    "Quiero información sobre precios",
    "Necesito más clientes",
    "Agendar consultoría gratuita"
  ];

  return (
    <>
      {/* Chat Widget Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 z-50 transition-all duration-200 hover:scale-110"
          aria-label="Abrir chat de asistencia"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-80 h-[500px] shadow-xl z-50 flex flex-col animate-scale-in">
          <CardHeader className="pb-3 bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                <CardTitle className="text-sm">Chat con IA - MKTN</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                aria-label="Cerrar chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col p-0">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4 max-h-80" ref={scrollAreaRef}>
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm break-words ${
                        message.sender === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {message.sender === 'ai' && <Bot className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                        {message.sender === 'user' && <User className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                        <span className="leading-relaxed whitespace-pre-wrap">{message.content}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Bot className="h-3 w-3" />
                        <div className="flex gap-1">
                          <div className="w-1 h-1 bg-muted-foreground rounded-full animate-pulse"></div>
                          <div className="w-1 h-1 bg-muted-foreground rounded-full animate-pulse delay-75"></div>
                          <div className="w-1 h-1 bg-muted-foreground rounded-full animate-pulse delay-150"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Quick Actions */}
            {messages.length <= 2 && (
              <div className="px-4 pb-2">
                <div className="text-xs text-muted-foreground mb-2">Preguntas frecuentes:</div>
                <div className="grid grid-cols-1 gap-1">
                  {quickActions.map((action, index) => (
                     <Button
                       key={index}
                       variant="ghost"
                       size="sm"
                       className="text-xs h-auto py-1 justify-start text-left"
                       onClick={() => sendMessage(action)}
                     >
                       {action}
                     </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu mensaje..."
                  disabled={isLoading}
                  className="text-sm"
                />
                 <Button
                   onClick={() => sendMessage()}
                   disabled={!input.trim() || isLoading}
                   size="sm"
                   className="px-3"
                   aria-label="Enviar mensaje"
                 >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-center mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => window.location.href = '/reservar-cita'}
                >
                  <Phone className="h-3 w-3 mr-1" />
                  ¿Prefieres hablar? Agenda una cita
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}